// src/services/notifyStream.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense Notify — ONE live connection per browser tab, shared by every component.
//
//   • Server-Sent Events (EventSource) with a short-lived ticket (the JWT never goes in a URL)
//   • reconnect with exponential backoff + jitter; a fresh ticket each time
//   • Last-Event-ID replay: notifications missed while offline are re-sent on reconnect
//   • liveness watchdog: no frame (the server pings every 25 s) for 70 s → reconnect
//   • reference counted: start() / stop() from hooks; closes when nothing listens
//   • falls back to 30 s polling ('poll' event) when EventSource is unavailable
//
//   import notifyStream from '../services/notifyStream';
//   const off = notifyStream.on('notification', ({ notification, silent }) => …);
// ─────────────────────────────────────────────────────────────────────────────
import { getStreamTicket, streamUrl } from './notifyApi';

const EVENTS = ['hello', 'notification', 'notification_update', 'summary', 'dispatch_update', 'session_replaced', 'reconnect', 'ping'];
const BACKOFF = [1000, 2000, 5000, 10000, 20000, 30000];
const WATCHDOG_MS = 70000;
const POLL_MS = 30000;

class NotifyStream {
    constructor() {
        this.listeners = new Map();
        this.refs = 0;
        this.es = null;
        this.status = 'idle';          // idle | connecting | live | reconnecting | offline | polling
        this.attempt = 0;
        this.lastEventId = null;
        this.retryTimer = null;
        this.watchdog = null;
        this.pollTimer = null;
        this.lastFrameAt = 0;
        this.hello = null;
        this.connectSeq = 0;
        this._onVisible = this._onVisible.bind(this);
        this._onOnline = this._onOnline.bind(this);
    }

    /** Subscribes to an event ('*' receives { event, data }). @returns {() => void} unsubscribe */
    on(event, fn) {
        if (!this.listeners.has(event)) this.listeners.set(event, new Set());
        this.listeners.get(event).add(fn);
        return () => this.listeners.get(event)?.delete(fn);
    }

    emit(event, data) {
        this.listeners.get(event)?.forEach((fn) => { try { fn(data); } catch (e) { /* a listener error must not break the stream */ } });
        if (event !== 'status') this.listeners.get('*')?.forEach((fn) => { try { fn({ event, data }); } catch (e) { /* ignore */ } });
    }

    getStatus() { return this.status; }

    _setStatus(s) {
        if (this.status === s) return;
        this.status = s;
        this.emit('status', s);
    }

    start() {
        this.refs += 1;
        if (this.refs === 1) {
            if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this._onVisible);
            if (typeof window !== 'undefined') window.addEventListener('online', this._onOnline);
            this._connect();
        }
        return () => this.stop();
    }

    stop() {
        this.refs = Math.max(0, this.refs - 1);
        if (this.refs === 0) {
            this._teardown();
            if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this._onVisible);
            if (typeof window !== 'undefined') window.removeEventListener('online', this._onOnline);
            this._setStatus('idle');
        }
    }

    /** Forces an immediate reconnect (e.g. after login). */
    restart() {
        this._teardown();
        this.attempt = 0;
        if (this.refs > 0) this._connect();
    }

    _teardown() {
        clearTimeout(this.retryTimer);
        clearInterval(this.watchdog);
        clearInterval(this.pollTimer);
        this.retryTimer = null;
        this.watchdog = null;
        this.pollTimer = null;
        this.connectSeq += 1;
        if (this.es) { try { this.es.close(); } catch (_) { /* ignore */ } }
        this.es = null;
    }

    async _connect() {
        const ES = typeof window !== 'undefined' ? window.EventSource : undefined;
        if (!ES) { this._startPolling(); return; }
        const seq = ++this.connectSeq;
        this._setStatus(this.attempt ? 'reconnecting' : 'connecting');
        let ticket;
        try {
            ticket = (await getStreamTicket())?.ticket;
        } catch (e) {
            if (seq !== this.connectSeq) return;
            this._scheduleRetry(e?.status === 401 || e?.status === 403 ? 60000 : null, e?.status === 401 ? 'offline' : 'reconnecting');
            return;
        }
        if (seq !== this.connectSeq || this.refs === 0) return;
        let es;
        try { es = new ES(streamUrl(ticket, this.lastEventId)); } catch (_) { this._startPolling(); return; }
        this.es = es;
        this.lastFrameAt = Date.now();

        const handle = (name) => (ev) => {
            this.lastFrameAt = Date.now();
            if (ev.lastEventId) this.lastEventId = ev.lastEventId;
            let payload = null;
            try { payload = ev.data ? JSON.parse(ev.data) : null; } catch (_) { payload = null; }
            if (name === 'hello') {
                this.attempt = 0;
                this.hello = payload;
                this._setStatus('live');
            }
            if (name === 'session_replaced') { this._teardown(); this._scheduleRetry(30000, 'reconnecting'); }
            if (name === 'reconnect') { this._teardown(); this._scheduleRetry(1500 + Math.random() * 2500, 'reconnecting'); }
            this.emit(name, payload);
        };
        EVENTS.forEach((name) => es.addEventListener(name, handle(name)));
        es.onerror = () => {
            if (seq !== this.connectSeq) return;
            // Always re-ticket ourselves (the browser's auto-retry would reuse an expired ticket)
            this._teardown();
            this._scheduleRetry(null, 'reconnecting');
        };
        clearInterval(this.watchdog);
        this.watchdog = setInterval(() => {
            if (Date.now() - this.lastFrameAt > WATCHDOG_MS) { this._teardown(); this._scheduleRetry(0, 'reconnecting'); }
        }, 15000);
    }

    _scheduleRetry(delay, status = 'reconnecting') {
        if (this.refs === 0) return;
        clearTimeout(this.retryTimer);
        const base = delay ?? BACKOFF[Math.min(this.attempt, BACKOFF.length - 1)];
        const wait = base + Math.round(Math.random() * Math.min(1000, base * 0.3));
        this.attempt += 1;
        this._setStatus(status);
        this.retryTimer = setTimeout(() => this._connect(), wait);
    }

    _startPolling() {
        this._setStatus('polling');
        clearInterval(this.pollTimer);
        this.emit('poll', { at: Date.now() });
        this.pollTimer = setInterval(() => this.emit('poll', { at: Date.now() }), POLL_MS);
    }

    _onVisible() {
        if (typeof document === 'undefined' || document.visibilityState !== 'visible' || this.refs === 0) return;
        if (this.status !== 'live' && this.status !== 'connecting' && this.status !== 'polling') { this.attempt = 0; this._teardown(); this._connect(); }
        this.emit('visible', { at: Date.now() });
    }

    _onOnline() {
        if (this.refs === 0 || this.status === 'live') return;
        this.attempt = 0;
        this._teardown();
        this._connect();
    }
}

const notifyStream = new NotifyStream();
export default notifyStream;
export { NotifyStream };
